import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import DashboardShell from './DashboardShell';
import StatCard from '../common/StatCard';
import HealthDot from '../common/HealthDot';
import PersonaBadge from '../common/PersonaBadge';
import DataTable from '../common/DataTable';
import HeatmapGrid from '../common/HeatmapGrid';
import api from '../../services/api';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useTheme } from '../../contexts/ThemeContext';
import { FiUser, FiZap, FiTarget, FiInbox } from 'react-icons/fi';

interface PersonaRow {
  id: string;
  name: string;
  tags: string[];
  revision: number;
  artifact_count: number;
  workflow_count: number;
  probe_pass_rate: number;
  last_active?: string;
  capabilities: Record<string, number>;
  is_forged: boolean;
}

const columns: ColumnDef<PersonaRow, any>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <PersonaBadge
        personaId={row.original.id}
        personaName={row.original.name}
        size="sm"
        health={row.original.is_forged ? 'green' : 'gray'}
      />
    ),
  },
  {
    accessorKey: 'tags',
    header: 'Tags',
    enableSorting: false,
    cell: ({ getValue }) => {
      const tags = getValue() as string[];
      return (
        <div className="flex gap-1 flex-wrap">
          {tags.slice(0, 3).map(t => (
            <span key={t} className="px-1.5 py-0.5 text-[10px] rounded bg-purple-600/20 text-purple-300">{t}</span>
          ))}
          {tags.length > 3 && <span className="text-[10px] text-gray-500">+{tags.length - 3}</span>}
        </div>
      );
    },
  },
  { accessorKey: 'revision', header: 'Rev', size: 60 },
  { accessorKey: 'artifact_count', header: 'Artifacts', size: 80 },
  { accessorKey: 'workflow_count', header: 'Workflows', size: 90 },
  {
    accessorKey: 'probe_pass_rate',
    header: 'Probes',
    size: 80,
    cell: ({ getValue }) => {
      const rate = getValue() as number;
      if (rate < 0) return <span className="text-gray-500">{'\u2014'}</span>;
      const color = rate >= 80 ? 'bg-green-500' : rate >= 50 ? 'bg-amber-500' : 'bg-red-500';
      return (
        <div className="flex items-center gap-1.5">
          <div className="flex-1 h-1.5 bg-gray-700 rounded-full max-w-[40px]">
            <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${rate}%` }} />
          </div>
          <span className="text-[10px]">{rate}%</span>
        </div>
      );
    },
  },
  {
    accessorKey: 'last_active',
    header: 'Last Active',
    cell: ({ getValue }) => {
      const val = getValue() as string | undefined;
      if (!val) return <span className="text-gray-500">—</span>;
      const diff = Date.now() - new Date(val).getTime();
      const days = Math.floor(diff / 86400000);
      if (days === 0) return 'Today';
      if (days === 1) return 'Yesterday';
      return `${days}d ago`;
    },
  },
  {
    accessorKey: 'capabilities',
    header: 'Capabilities',
    enableSorting: false,
    cell: ({ getValue }) => {
      const caps = getValue() as Record<string, number>;
      const vals = Object.values(caps);
      if (vals.length === 0) return <span className="text-gray-500">—</span>;
      const max = 10;
      return (
        <div className="flex gap-0.5 items-end h-4">
          {vals.slice(0, 6).map((v, i) => (
            <div
              key={i}
              className="w-1.5 rounded-t bg-purple-500/70"
              style={{ height: `${(v / max) * 16}px` }}
              title={`${v}/${max}`}
            />
          ))}
        </div>
      );
    },
  },
  {
    id: 'health',
    header: 'Health',
    size: 60,
    cell: ({ row }) => (
      <HealthDot
        status={row.original.is_forged ? 'green' : 'gray'}
        reason={row.original.is_forged ? 'Forged persona' : 'Legacy import'}
      />
    ),
  },
];

const PersonaDashboard: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [personas, setPersonas] = useState<PersonaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [heatmapData, setHeatmapData] = useState<{ date: string; value: number }[]>([]);

  const { summary } = useAnalytics({ entityType: 'persona' });

  // Load heatmap data from activity feed
  useEffect(() => {
    api.get('/analytics/cross-system/activity-feed?limit=500')
      .then(r => {
        const events = r.data?.events || [];
        const counts: Record<string, number> = {};
        events.forEach((e: any) => {
          if (e.system === 'persona' || e.persona_id) {
            const date = e.timestamp?.slice(0, 10);
            if (date) counts[date] = (counts[date] || 0) + 1;
          }
        });
        setHeatmapData(Object.entries(counts).map(([date, value]) => ({ date, value })));
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await api.get('/personas?limit=500');
        const data = resp.data?.personas || resp.data || [];
        setPersonas(data.map((p: any) => ({
          id: p.id,
          name: p.name,
          tags: p.tags || [],
          revision: p.data?.data?.engineering_profile?.current_state?.revision || 0,
          artifact_count: p.data?.portfolio_summary?.artifact_count || 0,
          workflow_count: p.data?.portfolio_summary?.workflow_count || 0,
          probe_pass_rate: (() => {
            const probes = p.data?.data?.engineering_profile?.current_state?.probes || [];
            if (probes.length === 0) return -1;
            const passed = probes.filter((pr: any) => pr.passed === true).length;
            return Math.round((passed / probes.length) * 100);
          })(),
          last_active: p.data?.portfolio_summary?.last_active || p.updated_at,
          capabilities: p.capabilities || p.data?.capabilities || {},
          is_forged: !!p.data?.data?.engineering_profile,
        })));
      } catch (err) {
        console.error('Failed to load personas:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const sectionClass = `rounded-lg shadow-lg border-l-4 p-5 ${
    darkMode ? 'bg-gray-800 border-purple-600' : 'bg-white border-purple-500 shadow-sm'
  }`;

  return (
    <DashboardShell title="Forge Monitor">
      {(dateRange) => (
        <div className="relative">
          {/* Ambient glow */}
          <div className="absolute -top-16 -left-16 w-56 h-56 bg-purple-600/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute top-20 -right-16 w-40 h-40 bg-blue-600/5 rounded-full blur-3xl pointer-events-none" />

          {/* Stat Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <StatCard label="Total Personas" value={summary?.total ?? '...'} icon={<FiUser className="w-4 h-4" />} trend={summary?.trend_7d} trendDirection={summary?.trend_7d?.[6] > summary?.trend_7d?.[0] ? 'up' : summary?.trend_7d?.[6] < summary?.trend_7d?.[0] ? 'down' : 'flat'} accentColor="purple" />
            <StatCard label="Active (30d)" value={summary?.active_30d ?? '...'} color="#22c55e" accentColor="purple" />
            <StatCard label="Forged" value={summary?.forged_count ?? '...'} color="#7c3aed" accentColor="purple" />
            <StatCard label="Avg Capability" value={summary?.avg_capability_score ?? '...'} color="#6366f1" accentColor="purple" />
            <StatCard label="Artifacts" value={summary?.total_artifacts_generated ?? '...'} icon={<FiZap className="w-4 h-4" />} accentColor="purple" />
            <StatCard label="Suggestions" value={summary?.suggestion_queue_depth ?? '...'} icon={<FiInbox className="w-4 h-4" />} accentColor="purple" />
          </div>

          {/* Activity Heatmap */}
          {heatmapData.length > 0 && (
            <div className={`${sectionClass} mb-6`}>
              <h2 className="text-sm font-semibold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-400">
                90-Day Activity
              </h2>
              <HeatmapGrid data={heatmapData} days={90} />
            </div>
          )}

          {/* Forge Funnel + Rising Stars */}
          {personas.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Forge Funnel */}
              <div className={`${sectionClass} col-span-2`}>
                <h2 className="text-sm font-semibold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-400">
                  Forge Funnel
                </h2>
                {(() => {
                  const total = personas.length;
                  const forged = personas.filter(p => p.is_forged).length;
                  const withArtifacts = personas.filter(p => p.artifact_count > 0).length;
                  const withWorkflows = personas.filter(p => p.workflow_count > 0).length;
                  const stages = [
                    { label: 'Total Personas', count: total, color: 'from-gray-500 to-gray-400' },
                    { label: 'Forged', count: forged, color: 'from-purple-600 to-purple-500' },
                    { label: 'Generated Artifacts', count: withArtifacts, color: 'from-blue-600 to-blue-500' },
                    { label: 'In Workflows', count: withWorkflows, color: 'from-cyan-600 to-cyan-500' },
                  ];
                  const maxCount = Math.max(...stages.map(s => s.count), 1);
                  return (
                    <div className="space-y-2">
                      {stages.map((stage, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <span className={`text-xs w-32 text-right ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{stage.label}</span>
                          <div className="flex-1 h-5 relative">
                            <div
                              className={`h-5 rounded-r-lg bg-gradient-to-r ${stage.color} transition-all duration-500`}
                              style={{ width: `${(stage.count / maxCount) * 100}%`, minWidth: '24px' }}
                            />
                            <span className="absolute right-2 top-0 text-xs text-white font-bold leading-5">{stage.count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Rising Stars */}
              <div className={sectionClass}>
                <h2 className="text-sm font-semibold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-amber-400 to-orange-400">
                  Most Active
                </h2>
                <div className="space-y-2">
                  {personas
                    .filter(p => p.artifact_count > 0 || p.workflow_count > 0)
                    .sort((a, b) => (b.artifact_count + b.workflow_count) - (a.artifact_count + a.workflow_count))
                    .slice(0, 5)
                    .map(p => (
                      <div key={p.id} className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                        <PersonaBadge personaId={p.id} personaName={p.name} size="sm" />
                        <div className="flex-1" />
                        <span className="text-xs text-purple-400">{p.artifact_count}a</span>
                        <span className="text-xs text-blue-400">{p.workflow_count}w</span>
                      </div>
                    ))
                  }
                  {personas.filter(p => p.artifact_count > 0 || p.workflow_count > 0).length === 0 && (
                    <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No activity yet</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Persona Table */}
          <div className={sectionClass}>
            <h2 className="text-lg font-semibold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-400">
              All Personas
            </h2>
            <DataTable
              columns={columns}
              data={personas}
              isLoading={loading}
              searchable
              searchPlaceholder="Search personas by name or tag..."
              pageSize={20}
              emptyMessage="No personas found"
              onRowClick={(row) => navigate(`/dashboards/personas/${row.id}`)}
              expandableRow={(row) => (
                <div className={`flex items-center justify-between ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <div className="flex items-center gap-6 text-sm">
                    <span>Artifacts: <strong>{row.artifact_count}</strong></span>
                    <span>Workflows: <strong>{row.workflow_count}</strong></span>
                    <span>Rev: <strong>{row.revision}</strong></span>
                    <span>{row.is_forged ? 'Forged' : 'Legacy'}</span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/dashboards/personas/${row.id}`); }}
                    className="px-3 py-1.5 text-xs rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 transition-all"
                  >
                    Open Full View <span className="inline-block transition-transform group-hover:translate-x-1">&rarr;</span>
                  </button>
                </div>
              )}
            />
          </div>
        </div>
      )}
    </DashboardShell>
  );
};

export default PersonaDashboard;
